import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  AddShoppingCart,
  CameraAlt,
  CheckCircle,
  CloudUpload,
  ExpandLess,
  ExpandMore,
  PhotoCamera,
  Replay,
  Search,
} from '@material-ui/icons';
import { useEffect, useRef, useState } from 'react';
import { addToQueueWithQuantity, searchByLabel, scanLabelImage, updatePrintQueue } from '../../Actions';
import { formatDttm, dttmToEpoch } from '../../Util/timezone';

// Max possible score: style(5) + manufacturer(4) + sku(4) + style_name(3) + color(2) + material(1) = 19
const MAX_SCORE = 19;

function MatchStrength({ score }) {
  const pct = Math.round((score / MAX_SCORE) * 100);
  const color = score >= 7 ? '#4caf50' : score >= 3 ? '#ff9800' : '#9e9e9e';
  return (
    <Tooltip title={`Match score: ${score}/${MAX_SCORE}`}>
      <div style={{ width: '48px', flexShrink: 0, position: 'relative', height: '6px' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '3px', backgroundColor: '#e0e0e0' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, borderRadius: '3px', width: `${pct}%`, backgroundColor: color }} />
      </div>
    </Tooltip>
  );
}

const FIELDS = [
  { key: 'manufacturer',    label: 'Manufacturer',            bodyKey: 'manufacturer' },
  { key: 'style_number',    label: 'Style Number',            bodyKey: 'style_number' },
  { key: 'style_name',      label: 'Style Name / Trade Name', bodyKey: 'style_name' },
  { key: 'material_class',  label: 'Material Class',          bodyKey: 'material_class' },
  { key: 'sku',             label: 'SKU',                     bodyKey: 'sku' },
  { key: 'color',           label: 'Color',                   bodyKey: 'color' },
];

const EMPTY_FIELDS = {
  manufacturer:   '',
  style_number:   '',
  style_name:     '',
  material_class: '',
  sku:            '',
  color:          '',
};

export default function LabelScanner() {
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [preview, setPreview]           = useState(null);
  const [imageFile, setImageFile]       = useState(null);
  const [fields, setFields]             = useState(null);
  const [scanning, setScanning]         = useState(false);
  const [searching, setSearching]       = useState(false);
  const [matches, setMatches]           = useState(null);
  const [selected, setSelected]         = useState(new Set());
  const [queuedCounts, setQueuedCounts] = useState({});
  const [quantities, setQuantities]     = useState({});
  const [adding, setAdding]             = useState(new Set());
  const [expanded, setExpanded]         = useState(new Set());
  const [scanError, setScanError]       = useState(null);
  const [searchError, setSearchError]   = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [manualMode, setManualMode]     = useState(false);

  const fileInputRef = useRef(null);
  const videoRef     = useRef(null);
  const canvasRef    = useRef(null);

  // Wire stream to video element once both exist
  useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  // Stop camera tracks on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    };
  }, [cameraStream]);

  // ── Image handling ──────────────────────────────────────────────────────────

  const processFile = (file) => {
    if (!file) return;
    setImageFile(file);
    setFields(null);
    setMatches(null);
    setSelected(new Set());
    setScanError(null);
    setSearchError(null);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
    scanFile(file);
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);

  const startCamera = async () => {
    setScanError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      setCameraStream(stream);
      setCameraActive(true);
    } catch {
      setScanError('Camera access denied or unavailable. Use the upload option instead.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    setCameraStream(null);
    setCameraActive(false);
  };

  const capturePhoto = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      stopCamera();
      processFile(new File([blob], 'label-capture.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const handleReset = () => {
    stopCamera();
    setPreview(null);
    setImageFile(null);
    setFields(null);
    setMatches(null);
    setSelected(new Set());
    setScanError(null);
    setSearchError(null);
    setManualMode(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Skip the photo entirely and search by typing the fields in by hand.
  const startManualEntry = () => {
    handleReset();
    setManualMode(true);
    setFields({ ...EMPTY_FIELDS });
  };

  // ── Scan ────────────────────────────────────────────────────────────────────

  const scanFile = async (file) => {
    setScanning(true);
    setScanError(null);
    setFields(null);
    setMatches(null);
    setSelected(new Set());
    try {
      const { data } = await scanLabelImage(file);
      const x = data.extracted;
      const newFields = {
        manufacturer:   x.manufacturer    ?? '',
        style_number:   x.style_number    ?? '',
        style_name:     x.style_name      ?? '',
        material_class: x.material_class  ?? '',
        sku:            x.sku             ?? '',
        color:          x.color           ?? '',
      };
      setFields(newFields);
      doSearch(newFields);
    } catch (err) {
      setScanError(err?.response?.data?.message || 'Failed to scan label. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleRescan = () => { if (imageFile) scanFile(imageFile); };

  // ── Search ──────────────────────────────────────────────────────────────────

  const doSearch = async (searchFields) => {
    if (!searchFields || !FIELDS.some(({ key }) => searchFields[key]?.trim())) return;
    setSearching(true);
    setSearchError(null);
    setMatches(null);
    setSelected(new Set());
    setExpanded(new Set());
    try {
      const { data } = await searchByLabel({
        manufacturer:   searchFields.manufacturer,
        style_number:   searchFields.style_number,
        style_name:     searchFields.style_name,
        material_class: searchFields.material_class,
        sku:            searchFields.sku,
        color:          searchFields.color,
      });
      setMatches(Array.isArray(data.matches) ? data.matches : []);
    } catch {
      setSearchError('Failed to search products. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = () => doSearch(fields);

  // Editable field grid + search button — shared by the scanned and manual flows.
  const renderFieldsForm = (searchLabel) => (
    <>
      <Grid container spacing={1} style={{ marginBottom: '10px' }}>
        {FIELDS.map(({ key, label }) => (
          <Grid item xs={12} sm={6} key={key}>
            <TextField
              label={label}
              value={fields[key]}
              onChange={(e) => setFields(prev => ({ ...prev, [key]: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Grid>
        ))}
      </Grid>
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        size={isMobile ? 'large' : 'medium'}
        onClick={handleSearch}
        disabled={searching}
        startIcon={searching ? <CircularProgress size={18} color="inherit" /> : <Search />}
      >
        {searching ? 'Searching...' : searchLabel}
      </Button>
    </>
  );

  // ── Queue helpers ───────────────────────────────────────────────────────────

  const getQty = (uuid) => quantities[uuid] ?? 1;
  const setQty = (uuid, val) => {
    const n = Math.max(1, Math.min(50, parseInt(val) || 1));
    setQuantities(prev => ({ ...prev, [uuid]: n }));
  };

  const toggleSelect    = (uuid) => setSelected(prev => { const n = new Set(prev); n.has(uuid) ? n.delete(uuid) : n.add(uuid); return n; });
  const toggleExpand    = (uuid) => setExpanded(prev => { const n = new Set(prev); n.has(uuid) ? n.delete(uuid) : n.add(uuid); return n; });
  const toggleSelectAll = () => {
    if (!matches) return;
    const all = matches.map(m => m.product_uuid);
    setSelected(all.every(id => selected.has(id)) ? new Set() : new Set(all));
  };

  const addOne = async (uuid) => {
    const quantity = getQty(uuid);
    setAdding(prev => new Set([...prev, uuid]));
    try {
      await addToQueueWithQuantity(uuid, quantity);
      setQueuedCounts(prev => ({ ...prev, [uuid]: (prev[uuid] ?? 0) + quantity }));
      setQuantities(prev => ({ ...prev, [uuid]: 1 }));
    } catch { /* let user retry */ } finally {
      setAdding(prev => { const n = new Set(prev); n.delete(uuid); return n; });
    }
  };

  const addBulk = async (uuids) => {
    setAdding(prev => new Set([...prev, ...uuids]));
    try {
      await updatePrintQueue(uuids);
      setQueuedCounts(prev => { const n = { ...prev }; uuids.forEach(id => { n[id] = (n[id] ?? 0) + 1; }); return n; });
      setSelected(new Set());
    } catch { /* let user retry */ } finally {
      setAdding(prev => { const n = new Set(prev); uuids.forEach(id => n.delete(id)); return n; });
    }
  };

  const totalQueued = Object.values(queuedCounts).reduce((a, b) => a + b, 0);
  const allSelected = matches?.length > 0 && matches.every(m => selected.has(m.product_uuid));

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: isMobile ? '12px 10px' : '24px 20px', maxWidth: '900px', margin: '0 auto' }}>
      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>Label Scanner</Typography>

      <Paper style={{ padding: isMobile ? '12px' : '20px', marginBottom: '16px' }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        {/* Hidden canvas for photo capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* ── No image yet — show camera/upload choice ── */}
        {!preview && !cameraActive && !manualMode && (
          <div style={{ textAlign: 'center', padding: isMobile ? '16px 0' : '32px 0' }}>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              Take a photo of a product label or upload an existing image.
            </Typography>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px',
              justifyContent: 'center',
              alignItems: 'stretch',
              maxWidth: isMobile ? '280px' : 'none',
              margin: '0 auto',
            }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PhotoCamera />}
                onClick={startCamera}
                fullWidth={isMobile}
              >
                Use Camera
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth={isMobile}
              >
                Upload Image
              </Button>
            </div>
            <div style={{ marginTop: '12px' }}>
              <Button
                variant="text"
                size="small"
                startIcon={<Search />}
                onClick={startManualEntry}
                fullWidth={isMobile}
              >
                Skip — enter details manually
              </Button>
            </div>
            {scanError && (
              <Alert severity="error" onClose={() => setScanError(null)} style={{ marginTop: '16px', textAlign: 'left' }}>
                {scanError}
              </Alert>
            )}
          </div>
        )}

        {/* ── Manual entry — search without a photo ── */}
        {manualMode && !preview && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <Typography variant="body2" color="textSecondary">
                Enter product details to search — no photo needed.
              </Typography>
              <Button size="small" startIcon={<Replay />} onClick={handleReset}>
                Start Over
              </Button>
            </div>
            {fields && renderFieldsForm('Search')}
          </div>
        )}

        {/* ── Live camera view ── */}
        {cameraActive && (
          <div>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                maxHeight: isMobile ? '60vh' : '320px',
                objectFit: 'cover',
                borderRadius: '6px',
                backgroundColor: '#000',
              }}
            />
            <div style={{
              display: 'flex',
              gap: '8px',
              marginTop: '10px',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? 'large' : 'medium'}
                startIcon={<CameraAlt />}
                onClick={capturePhoto}
                fullWidth={isMobile}
              >
                Capture Photo
              </Button>
              <Button variant="outlined" onClick={stopCamera} fullWidth={isMobile}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* ── Image preview + scan controls ── */}
        {preview && (
          <Grid container spacing={isMobile ? 2 : 3} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <img
                src={preview}
                alt="Label preview"
                style={{ width: '100%', borderRadius: '6px', objectFit: 'contain', maxHeight: isMobile ? '200px' : '240px' }}
              />
              <Button size="small" startIcon={<Replay />} onClick={handleReset} style={{ marginTop: '6px' }}>
                Start Over
              </Button>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleRescan}
                disabled={scanning || searching}
                size={isMobile ? 'large' : 'medium'}
                startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <Replay />}
                style={{ marginBottom: '12px' }}
              >
                {scanning ? 'Scanning...' : 'Re-scan Label'}
              </Button>

              {scanError && (
                <Alert severity="error" onClose={() => setScanError(null)} style={{ marginBottom: '10px' }}>
                  {scanError}
                </Alert>
              )}

              {fields && renderFieldsForm('Search Again')}
            </Grid>
          </Grid>
        )}
      </Paper>

      {searchError && (
        <Alert severity="error" style={{ marginBottom: '16px' }} onClose={() => setSearchError(null)}>
          {searchError}
        </Alert>
      )}

      {searching && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">Searching products...</Typography>
        </div>
      )}

      {/* ── Results ── */}
      {matches !== null && (
        <Paper>
          {/* Results header */}
          <div style={{ padding: isMobile ? '10px 12px' : '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginRight: 'auto', fontSize: isMobile ? '14px' : undefined }}>
              {matches.length === 0
                ? 'No matching products found'
                : `${matches.length} match${matches.length === 1 ? '' : 'es'}, best first`}
              {totalQueued > 0 && (
                <Chip
                  label={`${totalQueued} queued`}
                  size="small"
                  color="primary"
                  style={{ marginLeft: '8px', verticalAlign: 'middle' }}
                />
              )}
            </Typography>

            {matches.length > 0 && (
              <>
                <FormControlLabel
                  control={<Checkbox checked={allSelected} indeterminate={selected.size > 0 && !allSelected} onChange={toggleSelectAll} size="small" />}
                  label={<Typography variant="body2">Select all</Typography>}
                  style={{ margin: 0 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<AddShoppingCart />}
                  disabled={selected.size === 0}
                  onClick={() => addBulk([...selected])}
                >
                  Add {selected.size > 0 ? `${selected.size} ` : ''}Selected
                </Button>
                {matches.length > 1 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddShoppingCart />}
                    onClick={() => addBulk(matches.map(m => m.product_uuid))}
                  >
                    Add All {matches.length}
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Result rows */}
          {matches.map((product, i) => {
            const uuid       = product.product_uuid;
            const count      = queuedCounts[uuid] ?? 0;
            const isAdding   = adding.has(uuid);
            const isChecked  = selected.has(uuid);
            const isExpanded = expanded.has(uuid);
            const skus       = product.skus ?? [];

            // Keep the raw stored value of the most-recently-modified sku so we
            // can render it in the user's timezone below (comparison is by the
            // storage-anchored epoch).
            const latestModified = skus.reduce((max, s) => {
              if (!s.last_modified_dttm) return max;
              if (!max || dttmToEpoch(s.last_modified_dttm) > dttmToEpoch(max)) {
                return s.last_modified_dttm;
              }
              return max;
            }, null);

            return (
              <div key={uuid}>
                {i > 0 && <Divider />}

                {isMobile ? (
                  /* ── Mobile card layout ── */
                  <div style={{ padding: '10px 12px', backgroundColor: count > 0 ? '#f0faf0' : undefined }}>
                    {/* Top row: checkbox + product info + expand toggle */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggleSelect(uuid)}
                        size="small"
                        style={{ padding: '4px', marginTop: '2px', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}>
                          <div style={{ minWidth: 0 }}>
                            <Typography variant="body1" style={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {product.manufacturer_name}
                            </Typography>
                            {product.style && (
                              <Typography variant="body2" color="textSecondary">{product.style}</Typography>
                            )}
                          </div>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpand(uuid)}
                            style={{ padding: '4px', flexShrink: 0, marginTop: '-2px' }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </div>
                        <Typography variant="body2" color="textSecondary" noWrap style={{ fontSize: '12px' }}>
                          {[product.trade_name, product.collection].filter(Boolean).join(' · ')}
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                          {product.material_display && (
                            <Chip label={product.material_display} size="small" style={{ fontSize: '11px', height: '18px' }} />
                          )}
                          <MatchStrength score={product.relevance_score} />
                          {count > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#4caf50' }}>
                              <CheckCircle style={{ fontSize: '16px' }} />
                              <Typography variant="caption" style={{ color: '#4caf50', fontWeight: 600 }}>×{count}</Typography>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action row: qty stepper + add button + view */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', marginLeft: '36px' }}>
                      <IconButton
                        onClick={() => setQty(uuid, getQty(uuid) - 1)}
                        disabled={getQty(uuid) <= 1 || isAdding}
                        style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', flexShrink: 0 }}
                      >
                        <Typography variant="body1" style={{ lineHeight: 1, fontWeight: 700 }}>−</Typography>
                      </IconButton>
                      <TextField
                        value={getQty(uuid)}
                        onChange={(e) => setQty(uuid, e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ min: 1, max: 50, style: { textAlign: 'center', padding: '8px 4px', width: '36px' } }}
                        variant="outlined"
                        style={{ width: '58px', flexShrink: 0 }}
                        disabled={isAdding}
                      />
                      <IconButton
                        onClick={() => setQty(uuid, getQty(uuid) + 1)}
                        disabled={getQty(uuid) >= 50 || isAdding}
                        style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', flexShrink: 0 }}
                      >
                        <Typography variant="body1" style={{ lineHeight: 1, fontWeight: 700 }}>+</Typography>
                      </IconButton>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isAdding}
                        onClick={() => addOne(uuid)}
                        startIcon={isAdding ? <CircularProgress size={14} color="inherit" /> : <AddShoppingCart />}
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        {isAdding ? 'Adding...' : `Add${getQty(uuid) > 1 ? ` ${getQty(uuid)}` : ''}`}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        href={`/product/${uuid}`}
                        target="_blank"
                        rel="noopener"
                        style={{ flexShrink: 0, fontSize: '11px', padding: '6px 8px' }}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* ── Desktop row layout ── */
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    gap: '8px',
                    backgroundColor: count > 0 ? '#f0faf0' : undefined,
                  }}>
                    <Checkbox checked={isChecked} onChange={() => toggleSelect(uuid)} size="small" style={{ padding: '4px' }} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>{product.manufacturer_name}</Typography>
                        {product.style && <Typography variant="body2" color="textSecondary">{product.style}</Typography>}
                        {product.material_display && (
                          <Chip label={product.material_display} size="small" style={{ fontSize: '11px', height: '18px' }} />
                        )}
                      </div>
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {[product.trade_name, product.collection].filter(Boolean).join(' · ')}
                      </Typography>
                    </div>

                    <MatchStrength score={product.relevance_score} />

                    {count > 0 && (
                      <Tooltip title={`${count} in queue`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#4caf50', flexShrink: 0 }}>
                          <CheckCircle fontSize="small" />
                          <Typography variant="caption" style={{ color: '#4caf50', fontWeight: 600 }}>×{count}</Typography>
                        </div>
                      </Tooltip>
                    )}

                    {/* Quantity stepper */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => setQty(uuid, getQty(uuid) - 1)} disabled={getQty(uuid) <= 1 || isAdding} style={{ padding: '2px' }}>
                        <Typography variant="body2" style={{ lineHeight: 1, fontWeight: 700 }}>−</Typography>
                      </IconButton>
                      <TextField
                        value={getQty(uuid)}
                        onChange={(e) => setQty(uuid, e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ min: 1, max: 50, style: { textAlign: 'center', padding: '4px', width: '32px' } }}
                        variant="outlined"
                        style={{ width: '52px' }}
                        disabled={isAdding}
                      />
                      <IconButton size="small" onClick={() => setQty(uuid, getQty(uuid) + 1)} disabled={getQty(uuid) >= 50 || isAdding} style={{ padding: '2px' }}>
                        <Typography variant="body2" style={{ lineHeight: 1, fontWeight: 700 }}>+</Typography>
                      </IconButton>
                    </div>

                    <Tooltip title={`Add ${getQty(uuid)} to print queue`}>
                      <span>
                        <IconButton size="small" color="primary" disabled={isAdding} onClick={() => addOne(uuid)}>
                          {isAdding ? <CircularProgress size={18} /> : <AddShoppingCart fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Button size="small" variant="text" href={`/product/${uuid}`} target="_blank" rel="noopener" style={{ flexShrink: 0, fontSize: '11px' }}>
                      View
                    </Button>

                    <Tooltip title={isExpanded ? 'Hide details' : `Show ${skus.length} color${skus.length === 1 ? '' : 's'}`}>
                      <IconButton size="small" onClick={() => toggleExpand(uuid)} style={{ padding: '2px', flexShrink: 0 }}>
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </div>
                )}

                {/* Expandable SKU detail — shared between mobile and desktop */}
                <Collapse in={isExpanded}>
                  <div style={{
                    padding: isMobile ? '6px 12px 12px 36px' : '4px 16px 12px 52px',
                    backgroundColor: '#fafafa',
                    borderTop: '1px solid #f0f0f0',
                  }}>
                    {latestModified && (
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: '6px' }}>
                        Last modified: {formatDttm(latestModified, { format: 'MMM D, YYYY' })}
                      </Typography>
                    )}
                    {skus.length === 0 ? (
                      <Typography variant="caption" color="textSecondary">No SKU details available</Typography>
                    ) : (
                      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '480px' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', fontSize: '11px', color: '#757575', padding: '0 12px 4px 0', fontWeight: 500 }}>Color</th>
                            <th style={{ textAlign: 'left', fontSize: '11px', color: '#757575', padding: '0 0 4px 0', fontWeight: 500 }}>SKU</th>
                          </tr>
                        </thead>
                        <tbody>
                          {skus.map((s, idx) => (
                            <tr key={idx} style={{ backgroundColor: idx % 2 === 1 ? '#f0f0f0' : 'transparent' }}>
                              <td style={{ fontSize: '12px', padding: '3px 12px 3px 0' }}>{s.color_name ?? '—'}</td>
                              <td style={{ fontSize: '12px', padding: '3px 0', fontFamily: 'monospace' }}>{s.sku ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </Collapse>
              </div>
            );
          })}

          {matches.length === 25 && (
            <div style={{ padding: isMobile ? '10px 12px' : '10px 16px', borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="textSecondary">
                Showing top 25 results — refine the fields above and search again to narrow results.
              </Typography>
            </div>
          )}
        </Paper>
      )}
    </div>
  );
}
